/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Storage Panel Component
 * Manages AI System storage items (text records, files, images)
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as TextIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { storageApi } from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import type { AISystemStorage, AISystemStorageListItem, StorageType } from '../../types';

interface StoragePanelProps {
  systemId: number;
}

interface StorageFormData {
  name: string;
  storage_type: StorageType;
  content: string;
  tags: string[];
  file: File | null;
}

const initialFormData: StorageFormData = {
  name: '',
  storage_type: 'text',
  content: '',
  tags: [],
  file: null,
};

const StoragePanel: React.FC<StoragePanelProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<StorageType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AISystemStorage | null>(null);
  const [formData, setFormData] = useState<StorageFormData>(initialFormData);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState<AISystemStorage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<AISystemStorageListItem | null>(null);
  const [tagInput, setTagInput] = useState('');

  // Fetch storage items
  const { data: storageData, isLoading, error } = useQuery({
    queryKey: ['ai-storage', systemId, filterType, searchQuery],
    queryFn: () => storageApi.list(systemId, {
      storage_type: filterType !== 'all' ? filterType : undefined,
      search: searchQuery || undefined,
    }),
    enabled: !!systemId,
  });

  const items = storageData?.results || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => storageApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-storage', systemId] });
      toast.success('Storage item created');
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create storage item');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => storageApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-storage', systemId] });
      toast.success('Storage item updated');
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update storage item');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => storageApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-storage', systemId] });
      toast.success('Storage item deleted');
      setDeleteDialogOpen(false);
      setDeletingItem(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete storage item');
    },
  });

  const handleOpenDialog = (item?: AISystemStorageListItem) => {
    if (item) {
      // Fetch full item for editing
      storageApi.get(item.id).then((fullItem) => {
        setEditingItem(fullItem);
        setFormData({
          name: fullItem.name,
          storage_type: fullItem.storage_type,
          content: fullItem.content || '',
          tags: fullItem.tags || [],
          file: null,
        });
        setDialogOpen(true);
      });
    } else {
      setEditingItem(null);
      setFormData(initialFormData);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData(initialFormData);
    setTagInput('');
  };

  const handleViewItem = async (item: AISystemStorageListItem) => {
    try {
      const fullItem = await storageApi.get(item.id);
      setViewingItem(fullItem);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load item content');
    }
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (formData.storage_type === 'text' && !formData.content.trim()) {
      toast.error('Content is required for text items');
      return;
    }

    if ((formData.storage_type === 'file' || formData.storage_type === 'image') && !formData.file && !editingItem) {
      toast.error('File is required for file/image items');
      return;
    }

    const submitData: any = {
      ai_system: systemId,
      name: formData.name,
      storage_type: formData.storage_type,
      tags: formData.tags,
    };

    if (formData.storage_type === 'text') {
      submitData.content = formData.content;
    } else if (formData.file) {
      submitData.file = formData.file;
    }

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData({ ...formData, file });
    }
  };

  const getTypeIcon = (type: StorageType) => {
    switch (type) {
      case 'text': return <TextIcon />;
      case 'file': return <FileIcon />;
      case 'image': return <ImageIcon />;
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    return formatStoredDateTime(dateStr, undefined, 'N/A');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load storage items: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Storage</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Item
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search storage..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Tabs
            value={filterType}
            onChange={(_, v) => setFilterType(v)}
            sx={{ minHeight: 36 }}
          >
            <Tab label="All" value="all" sx={{ minHeight: 36, py: 0 }} />
            <Tab icon={<TextIcon />} label="Text" value="text" sx={{ minHeight: 36, py: 0 }} />
            <Tab icon={<FileIcon />} label="Files" value="file" sx={{ minHeight: 36, py: 0 }} />
            <Tab icon={<ImageIcon />} label="Images" value="image" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>
        </Stack>
      </Paper>

      {/* Items Table */}
      {items.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" gutterBottom>
                No storage items yet
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Add Your First Item
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {getTypeIcon(item.storage_type)}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="medium">{item.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.content_preview}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {item.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                      {item.tags.length > 3 && (
                        <Chip label={`+${item.tags.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(item.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleViewItem(item)}>
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeletingItem(item);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Storage Item' : 'Add Storage Item'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              helperText="Unique identifier for this storage item"
            />

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.storage_type}
                label="Type"
                onChange={(e) => setFormData({ ...formData, storage_type: e.target.value as StorageType })}
                disabled={!!editingItem}
              >
                <MenuItem value="text">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextIcon /> <span>Text Record</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="file">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FileIcon /> <span>File/Document</span>
                  </Stack>
                </MenuItem>
                <MenuItem value="image">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ImageIcon /> <span>Image</span>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            {formData.storage_type === 'text' ? (
              <TextField
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                multiline
                rows={10}
                fullWidth
                required
              />
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {formData.file ? formData.file.name : 'Choose File'}
                  <input
                    type="file"
                    hidden
                    accept={formData.storage_type === 'image' ? 'image/*' : '*'}
                    onChange={handleFileChange}
                  />
                </Button>
                {editingItem?.file_url && !formData.file && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Current file: {editingItem.metadata?.file_name || 'Unknown'}
                  </Typography>
                )}
              </Box>
            )}

            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Tags</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                {formData.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={handleAddTag}>Add</Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingItem ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              {viewingItem && getTypeIcon(viewingItem.storage_type)}
              <span>{viewingItem?.name}</span>
            </Stack>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewingItem && (
            <Box>
              <Stack direction="row" spacing={1} mb={2}>
                {viewingItem.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" />
                ))}
              </Stack>

              {viewingItem.storage_type === 'text' ? (
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                  <Typography
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}
                  >
                    {viewingItem.content}
                  </Typography>
                </Paper>
              ) : viewingItem.storage_type === 'image' && viewingItem.file_url ? (
                <Box textAlign="center">
                  <img
                    src={viewingItem.file_url}
                    alt={viewingItem.name}
                    style={{ maxWidth: '100%', maxHeight: 400 }}
                  />
                </Box>
              ) : viewingItem.file_url ? (
                <Box textAlign="center" py={4}>
                  <FileIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                  <Typography gutterBottom>
                    {viewingItem.metadata?.file_name || 'File'}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    href={viewingItem.file_url}
                    target="_blank"
                  >
                    Download
                  </Button>
                </Box>
              ) : (
                <Typography color="text.secondary">No content available</Typography>
              )}

              <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                Created: {formatDate(viewingItem.created_at)} | Updated: {formatDate(viewingItem.updated_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Storage Item</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deletingItem?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deletingItem && deleteMutation.mutate(deletingItem.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoragePanel;
